import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Booking, Listing, ContactInfo } from '@/types';
import { BookingForm as BookingFormData, BookingsService } from '@/utils/bookings-service';
import { ListingsService } from '@/utils/listings-service';
import { PhoneNumberInput } from '@/components/common/PhoneNumberInput';
import { formatDateToLocalString } from '@/utils/calendar-utils';

interface BookingFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (bookingData: BookingFormData) => Promise<void>;
  booking?: Booking | null;
  listings?: Listing[];
  isEditMode?: boolean; // Override the edit/create mode detection
}

export const BookingForm: React.FC<BookingFormProps> = ({
  open,
  onClose,
  onSubmit,
  booking,
  listings = [],
  isEditMode = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState<BookingFormData>({
    listingId: '',
    guestId: '',
    guestName: '',
    guestEmail: '',
    startDate: '',
    endDate: '',
    guests: 1,
    totalPrice: 0,
    useCustomPricing: false,
    customPriceBreakdown: {
      basePrice: 0,
      cleaningFee: 0,
      serviceFee: 0,
      taxes: 0,
      discounts: 0,
      notes: '',
    },
    primaryGuestContact: {
      whatsappNumber: '',
      preferredMethod: 'email',
    },
    expectedGuests: 1,
  });
  const [availableListings, setAvailableListings] = useState<Listing[]>(listings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [whatsappValid, setWhatsappValid] = useState(true);

  // Load and filter listings
  useEffect(() => {
    if (open) {
      if (listings.length === 0) {
        loadListings();
      } else {
        // Filter provided listings to only show published ones
        setAvailableListings(listings.filter(listing => listing.status === 'published'));
      }
    }
  }, [open, listings]);

  // Populate form when editing existing booking
  useEffect(() => {
    if (booking) {
      setFormData({
        listingId: booking.listingId,
        guestId: booking.guestId,
        guestName: booking.primaryGuestContact?.name || '',
        guestEmail: booking.primaryGuestContact?.email || '', // Use the actual email from primaryGuestContact
        startDate: booking.startDate,
        endDate: booking.endDate,
        guests: booking.guests,
        totalPrice: booking.totalPrice,
        useCustomPricing: false, // Default to standard pricing when editing
        customPriceBreakdown: {
          basePrice: 0,
          cleaningFee: 0,
          serviceFee: 0,
          taxes: 0,
          discounts: 0,
          notes: '',
        },
        primaryGuestContact: booking.primaryGuestContact || {
          whatsappNumber: '',
          preferredMethod: 'email',
        },
        expectedGuests: booking.expectedGuests || booking.guests,
      });
    } else {
      // Reset form for new booking
      setFormData({
        listingId: '',
        guestId: '',
        guestName: '',
        guestEmail: '',
        startDate: '',
        endDate: '',
        guests: 1,
        totalPrice: 0,
        useCustomPricing: false,
        customPriceBreakdown: {
          basePrice: 0,
          cleaningFee: 0,
          serviceFee: 0,
          taxes: 0,
          discounts: 0,
          notes: '',
        },
        primaryGuestContact: {
          whatsappNumber: '',
          preferredMethod: 'email',
        },
        expectedGuests: 1,
      });
    }
    setError(null);
    setAvailabilityError(null);
  }, [booking, open]);

  // Trigger price calculation when form is populated with booking data

  const loadListings = async () => {
    try {
      const userListings = await ListingsService.getListings();
      setAvailableListings(userListings.filter(listing => listing.status === 'published'));
    } catch (err) {
      console.error('Failed to load listings:', err);
    }
  };

  const handleInputChange = (field: keyof BookingFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear availability error when dates change
    if (field === 'startDate' || field === 'endDate' || field === 'listingId') {
      setAvailabilityError(null);
    }

    // Calculate total price when dates or listing changes (only if not using custom pricing)
    if ((field === 'startDate' || field === 'endDate' || field === 'listingId') && !formData.useCustomPricing) {
      calculateTotalPrice(field === 'listingId' ? value : formData.listingId, 
                         field === 'startDate' ? value : formData.startDate,
                         field === 'endDate' ? value : formData.endDate);
    }

    // When switching to custom pricing, initialize with calculated price
    if (field === 'useCustomPricing' && value === true) {
      const listing = availableListings.find(l => l.listingId === formData.listingId);
      if (listing && formData.startDate && formData.endDate) {
        const duration = BookingsService.calculateDuration(formData.startDate, formData.endDate);
        const basePrice = listing.pricing.basePrice * duration;
        setFormData(prev => ({
          ...prev,
          customPriceBreakdown: {
            basePrice,
            cleaningFee: listing.pricing.cleaningFee || 0,
            serviceFee: listing.pricing.serviceFee || 0,
            taxes: 0,
            discounts: 0,
            notes: '',
          },
        }));
      }
    }

    // When switching back to standard pricing, recalculate
    if (field === 'useCustomPricing' && value === false) {
      calculateTotalPrice(formData.listingId, formData.startDate, formData.endDate);
    }

    // Auto-sync expected guests with total guests when guests field changes
    if (field === 'guests') {
      setFormData(prev => ({
        ...prev,
        expectedGuests: value,
      }));
    }
  };

  const handleContactChange = (field: keyof ContactInfo, value: any) => {
    setFormData(prev => {
      const updatedContact = {
        ...prev.primaryGuestContact,
        [field]: value,
      };

      // Automatically determine preferred method based on available contact info
      let preferredMethod: 'email' | 'whatsapp' | 'both' = 'email';
      if (updatedContact.whatsappNumber && updatedContact.whatsappNumber.trim()) {
        preferredMethod = 'both'; // Both email and WhatsApp available
      }

      return {
        ...prev,
        primaryGuestContact: {
          ...updatedContact,
          preferredMethod,
        },
      };
    });
  };

  const handleCustomPriceChange = (field: string, value: number | string) => {
    setFormData(prev => {
      const newBreakdown = {
        ...prev.customPriceBreakdown!,
        [field]: value,
      };
      
      // Calculate total from custom breakdown
      const total = (newBreakdown.basePrice || 0) + 
                   (newBreakdown.cleaningFee || 0) + 
                   (newBreakdown.serviceFee || 0) + 
                   (newBreakdown.taxes || 0) - 
                   (newBreakdown.discounts || 0);
      
      return {
        ...prev,
        customPriceBreakdown: newBreakdown,
        totalPrice: Math.max(0, total), // Ensure total is not negative
      };
    });
  };

  const validateGuestContact = (): string | null => {
    const { whatsappNumber } = formData.primaryGuestContact || {};
    
    // Email validation is now handled in main form validation since it's a top-level field
    // Validate email format for the main guestEmail field
    if (formData.guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestEmail)) {
      return 'Please enter a valid email address.';
    }

    // WhatsApp number is optional, but if provided, must be valid
    if (whatsappNumber && !whatsappValid) {
      return 'Please enter a valid WhatsApp number.';
    }

    return null;
  };

  const calculateTotalPrice = useCallback((listingId: string, startDate: string, endDate: string) => {
    if (!listingId || !startDate || !endDate || formData.useCustomPricing) return;

    const listing = availableListings.find(l => l.listingId === listingId);
    if (!listing) return;

    const duration = BookingsService.calculateDuration(startDate, endDate);
    const baseTotal = listing.pricing.basePrice * duration;
    const cleaningFee = listing.pricing.cleaningFee || 0;
    const serviceFee = listing.pricing.serviceFee || 0;
    const totalPrice = baseTotal + cleaningFee + serviceFee;

    setFormData(prev => ({
      ...prev,
      totalPrice,
    }));
  }, [formData.useCustomPricing, availableListings]);
  useEffect(() => {
    if (booking && !isEditMode && formData.listingId && formData.startDate && formData.endDate && !formData.useCustomPricing) {
      // Small delay to ensure listings are loaded
      const timer = setTimeout(() => {
        calculateTotalPrice(formData.listingId, formData.startDate, formData.endDate);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.listingId, formData.startDate, formData.endDate, formData.useCustomPricing, booking, isEditMode, availableListings, calculateTotalPrice]);

  const checkAvailability = async () => {
    if (!formData.listingId || !formData.startDate || !formData.endDate) return;

    setCheckingAvailability(true);
    setAvailabilityError(null);

    try {
      const available = await BookingsService.checkAvailability(
        formData.listingId,
        formData.startDate,
        formData.endDate
      );

      if (!available) {
        setAvailabilityError('The selected dates are not available for this property.');
      }
    } catch (err) {
      setAvailabilityError('Failed to check availability. Please try again.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.listingId || !formData.guestName || !formData.guestEmail || !formData.startDate || !formData.endDate) {
      setError('Please fill in all required fields including guest name and email.');
      return;
    }

    // Validate guest contact information
    const contactError = validateGuestContact();
    if (contactError) {
      setError(contactError);
      return;
    }

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError('End date must be after start date.');
      return;
    }

    if (formData.guests < 1) {
      setError('Number of guests must be at least 1.');
      return;
    }

    if (formData.expectedGuests < 1 || formData.expectedGuests > formData.guests) {
      setError('Expected guests must be between 1 and the total number of guests.');
      return;
    }

    if (formData.guestName.trim().length < 2) {
      setError('Guest name must be at least 2 characters long.');
      return;
    }

    if (formData.useCustomPricing && formData.totalPrice <= 0) {
      setError('Total price must be greater than zero.');
      return;
    }

    const selectedListing = availableListings.find(l => l.listingId === formData.listingId);
    if (selectedListing && formData.guests > selectedListing.maxGuests) {
      setError(`This property can accommodate a maximum of ${selectedListing.maxGuests} guests.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ensure primaryGuestContact fields are set from main guest fields
      const submissionData = {
        ...formData,
        guestId: formData.guestEmail, // Use email as the guest ID
        primaryGuestContact: {
          ...formData.primaryGuestContact,
          name: formData.guestName, // Always use guestName for contact name
          email: formData.guestEmail, // Always use guestEmail for contact email
        },
      };

      await onSubmit(submissionData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedListing = availableListings.find(l => l.listingId === formData.listingId);
  const duration = formData.startDate && formData.endDate ? 
    BookingsService.calculateDuration(formData.startDate, formData.endDate) : 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {isEditMode ? 'Edit Booking' : 'Create New Booking'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {error && (
                <Alert severity="error">{error}</Alert>
              )}

              {availabilityError && (
                <Alert severity="warning">{availabilityError}</Alert>
              )}

              <FormControl fullWidth required>
                <InputLabel>Property</InputLabel>
                <Select
                  value={formData.listingId}
                  label="Property"
                  onChange={(e) => handleInputChange('listingId', e.target.value)}
                >
                  {availableListings.map((listing) => (
                    <MenuItem key={listing.listingId} value={listing.listingId}>
                      {listing.title} - {listing.address.city}, {listing.address.state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Guest Email"
                type="email"
                value={formData.guestEmail || ''}
                onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                required
                fullWidth
                helperText="Guest's email address (used as their ID in the system)"
              />

              <TextField
                label="Guest Name"
                value={formData.guestName}
                onChange={(e) => handleInputChange('guestName', e.target.value)}
                required
                fullWidth
                helperText="Full name of the primary guest"
              />

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Additional Contact Information
                </Typography>
              </Divider>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optional WhatsApp number for instant messaging.
              </Typography>

              <PhoneNumberInput
                label="WhatsApp Number (Optional)"
                value={formData.primaryGuestContact?.whatsappNumber || ''}
                onChange={(value, isValid) => {
                  handleContactChange('whatsappNumber', value);
                  setWhatsappValid(isValid || !value);
                }}
                placeholder="+1 (555) 123-4567"
                helperText="Optional WhatsApp number for instant messaging"
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Communication preference: {formData.primaryGuestContact?.whatsappNumber?.trim() ? 'Email + WhatsApp' : 'Email only'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Check-in Date"
                  value={formData.startDate ? new Date(formData.startDate) : null}
                  onChange={(date) => {
                    handleInputChange('startDate', date ? formatDateToLocalString(date) : '');
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                    },
                  }}
                  minDate={new Date()}
                />
                <DatePicker
                  label="Check-out Date"
                  value={formData.endDate ? new Date(formData.endDate) : null}
                  onChange={(date) => {
                    handleInputChange('endDate', date ? formatDateToLocalString(date) : '');
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                    },
                  }}
                  minDate={formData.startDate ? new Date(new Date(formData.startDate).getTime() + 86400000) : new Date()}
                />
              </Box>

              {formData.listingId && formData.startDate && formData.endDate && (
                <Button
                  onClick={checkAvailability}
                  disabled={checkingAvailability}
                  variant="outlined"
                  size="small"
                >
                  {checkingAvailability ? 'Checking...' : 'Check Availability'}
                </Button>
              )}

              <TextField
                label="Number of Guests"
                type="number"
                value={formData.guests}
                onChange={(e) => handleInputChange('guests', parseInt(e.target.value) || 1)}
                required
                fullWidth
                inputProps={{ 
                  min: 1, 
                  max: selectedListing?.maxGuests || 10 
                }}
                helperText={selectedListing ? `Maximum ${selectedListing.maxGuests} guests` : ''}
              />

              <TextField
                label="Expected Guests for Check-in"
                type="number"
                value={formData.expectedGuests}
                onChange={(e) => handleInputChange('expectedGuests', parseInt(e.target.value) || 1)}
                required
                fullWidth
                inputProps={{ 
                  min: 1, 
                  max: formData.guests || 10 
                }}
                helperText="Number of guests who will complete the check-in form"
              />

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Pricing
                </Typography>
              </Divider>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.useCustomPricing}
                    onChange={(e) => handleInputChange('useCustomPricing', e.target.checked)}
                  />
                }
                label="Use custom pricing instead of standard rates"
              />

              {!selectedListing || duration === 0 ? (
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {!formData.listingId ? 
                      'Select a property to view pricing options' : 
                      !formData.startDate || !formData.endDate ? 
                        'Select check-in and check-out dates to calculate pricing' :
                        'Loading pricing information...'
                    }
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {formData.useCustomPricing ? 'Custom Pricing' : 'Standard Pricing'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Property: {selectedListing.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Duration: {duration} night{duration !== 1 ? 's' : ''}
                  </Typography>

                  {!formData.useCustomPricing ? (
                    // Standard pricing display
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Base Rate: ${selectedListing.pricing.basePrice} × {duration} nights = ${selectedListing.pricing.basePrice * duration}
                      </Typography>
                      {selectedListing.pricing.cleaningFee && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Cleaning Fee: ${selectedListing.pricing.cleaningFee}
                        </Typography>
                      )}
                      {selectedListing.pricing.serviceFee && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Service Fee: ${selectedListing.pricing.serviceFee}
                        </Typography>
                      )}
                    </>
                  ) : (
                    // Custom pricing inputs
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Base Price"
                          type="number"
                          value={formData.customPriceBreakdown?.basePrice || 0}
                          onChange={(e) => handleCustomPriceChange('basePrice', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Cleaning Fee"
                          type="number"
                          value={formData.customPriceBreakdown?.cleaningFee || 0}
                          onChange={(e) => handleCustomPriceChange('cleaningFee', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Service Fee"
                          type="number"
                          value={formData.customPriceBreakdown?.serviceFee || 0}
                          onChange={(e) => handleCustomPriceChange('serviceFee', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Taxes"
                          type="number"
                          value={formData.customPriceBreakdown?.taxes || 0}
                          onChange={(e) => handleCustomPriceChange('taxes', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                      <TextField
                        label="Discounts"
                        type="number"
                        value={formData.customPriceBreakdown?.discounts || 0}
                        onChange={(e) => handleCustomPriceChange('discounts', parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        size="small"
                        helperText="Amount to subtract from total"
                      />
                      <TextField
                        label="Pricing Notes (Optional)"
                        value={formData.customPriceBreakdown?.notes || ''}
                        onChange={(e) => handleCustomPriceChange('notes', e.target.value)}
                        multiline
                        rows={2}
                        size="small"
                        helperText="Internal notes about custom pricing"
                      />
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" color="primary">
                    Total: ${formData.totalPrice.toFixed(2)}
                  </Typography>
                  
                  {formData.useCustomPricing && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Custom pricing applied. Standard rate would be: ${((selectedListing.pricing.basePrice * duration) + (selectedListing.pricing.cleaningFee || 0) + (selectedListing.pricing.serviceFee || 0)).toFixed(2)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || checkingAvailability || !!availabilityError || !whatsappValid}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Booking' : 'Create Booking'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BookingForm;